<template>
    <section v-if="!selectedOnly || selected.length" class="reusable-photos" :aria-busy="loading ? 'true' : 'false'">
        <div class="reusable-photos__head">
            <div>
                <h6>{{ selectedOnly ? 'Ранее загруженные фото в отчёте' : 'Уже загружены за этот день' }}</h6>
                <p v-if="!selectedOnly">Можно использовать фото из других отчётов этого объекта. Повторная загрузка не нужна.</p>
                <small class="reusable-photos__count" v-if="selected.length || selectable.length">
                    <span v-if="!selectedOnly">Доступно {{ selectable.length }} · </span>Выбрано: {{ selected.length }}
                </small>
            </div>
            <div v-if="!selectedOnly" class="reusable-photos__actions">
                <button v-if="selectable.length" type="button" class="btn btn-sm btn-outline-primary"
                    :disabled="loading || selected.length === selectable.length" @click="selectAll">Использовать все</button>
                <button v-if="selected.length" type="button" class="btn btn-sm btn-outline-secondary" @click="setSelection([])">Снять выбор</button>
            </div>
        </div>
        <div v-if="!selectedOnly && (lookup.error || saveError)" class="reusable-photos__error" role="alert">
            <span>{{ saveError || lookup.error }}</span>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="retry">Повторить</button>
        </div>
        <p v-if="loading && !selectedOnly" class="text-muted mb-0" role="status">Подбираем ранее загруженные фото…</p>
        <p v-else-if="!items.length && !selectedOnly && lookup.status === 'ready'" class="text-muted mb-0">В других отчётах за этот день фотографий пока нет.</p>
        <div v-if="items.length" class="reusable-photos__grid">
            <article v-for="file in items" :key="file.id" class="reusable-photos__tile" :class="{'is-selected': isSelected(file)}">
                <button type="button" class="reusable-photos__preview" :aria-label="'Посмотреть ' + file.name" @click="preview(file)">
                    <img v-if="file.isImage" :src="authenticated(file.thumbnailUrl)" :alt="file.name" loading="lazy">
                    <span v-else class="reusable-photos__video"><i class="fas fa-play-circle"></i> Видео</span>
                    <span class="reusable-photos__zoom">Посмотреть</span>
                    <span v-if="isSelected(file)" class="reusable-photos__selected" aria-hidden="true">✓</span>
                </button>
                <div class="reusable-photos__body">
                    <div class="reusable-photos__sources">Уже используется:
                        <span v-for="report in reports(file)" :key="report.id" class="reusable-photos__source">
                            <router-link :to="'/reports/' + report.id" target="_blank">№{{ report.id }}</router-link>
                            <small :class="report.annulled ? 'reusable-photos__annulled' : 'reusable-photos__active'">{{ report.annulled === true ? 'аннулирован' : report.annulled === false ? 'действующий' : 'статус уточняется' }}</small>
                        </span>
                    </div>
                    <label v-if="!selectedOnly" class="reusable-photos__check">
                        <input type="checkbox" :checked="isSelected(file)" :disabled="!file.selectable || loading" @change="toggle(file)">
                        <span>{{ isSelected(file) ? 'Включено в отчёт' : file.selectable ? 'Использовать' : 'Пока недоступно' }}</span>
                    </label>
                    <small v-if="!file.selectable && !selectedOnly" class="text-muted">{{ file.unavailableReason }}</small>
                </div>
            </article>
        </div>
        <p v-if="selected.length" class="reusable-photos__note">В других отчётах фото останутся доступны. Повторное использование будет записано в истории.</p>
        <p v-if="items.some(file => file.storageReadOnly)" class="reusable-photos__note">Тестовая среда: файлы общего хранилища используются без переименования.</p>
    </section>
</template>

<script>
export default {
    name: 'ReusableReportPhotos',
    props: {selectedOnly: {type: Boolean, default: false}},
    data: () => ({saveError: ''}),
    computed: {
        report() { return this.$store.state.shiftReport; },
        selected() { return this.report.reusedPhotos || []; },
        lookup() { return this.report.reusablePhotoLookup || {status: 'idle', items: []}; },
        items() { return this.selectedOnly ? this.selected : this.lookup.items || []; },
        selectable() { return this.items.filter(file => file.selectable); },
        loading() { return this.lookup.status === 'loading'; },
        contextKey() {
            const user = this.$store.getters.currentUser || {};
            return `${user.company}:${user.userId || user.id}:${this.report.selectedProcess}:${this.report.startDate}`;
        }
    },
    watch: {
        contextKey: {immediate: true, handler() { if (!this.selectedOnly) this.reload(); }}
    },
    methods: {
        reload() { this.saveError = ''; return this.$store.dispatch('shiftReport_loadReusablePhotos'); },
        retry() { return this.saveError ? this.setSelection(this.selected) : this.reload(); },
        reports(file) { return file.usedInReports || [{id: file.sourceReportId, annulled: null}]; },
        authenticated(url) {
            const token = this.$store.state.token;
            return url + (token ? `?gjuid=${encodeURIComponent(token)}` : '');
        },
        isSelected(file) { return this.selected.some(item => Number(item.id) === Number(file.id)); },
        async setSelection(files) {
            this.$store.commit('shiftReport_setReusedPhotos', files);
            try { await this.$store.dispatch('shiftReport_updateCache'); this.saveError = ''; }
            catch (_) { this.saveError = 'Выбор сохранён на экране, но черновик пока не обновлён.'; }
        },
        toggle(file) {
            if (!file.selectable || this.loading) return;
            this.setSelection(this.isSelected(file) ? this.selected.filter(item => item.id !== file.id) : this.selected.concat(file));
        },
        selectAll() { this.setSelection(this.selectable); },
        preview(file) {
            const files = this.items.map(item => ({...item, url: this.authenticated(item.url), downloadUrl: this.authenticated(item.url),
                thumbnailUrl: this.authenticated(item.thumbnailUrl)}));
            if (this.$fileViewer) this.$fileViewer.open(files, {startIndex: this.items.indexOf(file), source: 'reports', continuous: true});
            else window.open(this.authenticated(file.url), '_blank', 'noopener');
        }
    }
};
</script>

<style scoped>
.reusable-photos {background:rgba(247,251,253,.96);border:1px solid #dce8ed;border-radius:14px;padding:16px;margin-bottom:16px;box-shadow:inset 0 1px 0 #fff,0 3px 12px rgba(40,70,90,.04)}
.reusable-photos__head {display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:12px}
.reusable-photos__head h6 {font-weight:600;margin-bottom:5px;color:#294759}
.reusable-photos__head p,.reusable-photos__note {font-size:13px;color:#5b6b76;margin:0}
.reusable-photos__head button {flex-shrink:0;min-height:34px}
.reusable-photos__actions {display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end;flex-shrink:0;max-width:100%}
.reusable-photos__count {display:inline-block;margin-top:8px;font-weight:600;color:#286559}
.reusable-photos__grid {display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:12px}
.reusable-photos__tile {border:1px solid #dce5e8;border-radius:10px;overflow:hidden;background:#fff;min-width:0}
.reusable-photos__tile.is-selected {border-color:#70aaa0;box-shadow:0 0 0 1px #70aaa0}
.reusable-photos__preview {border:0;background:#e8eff2;display:block;position:relative;width:100%;height:130px;padding:0;cursor:zoom-in}
.reusable-photos__preview img {width:100%;height:100%;object-fit:cover}
.reusable-photos__zoom {position:absolute;right:6px;bottom:6px;background:rgba(255,255,255,.95);color:#324f61;font-size:11px;padding:3px 6px;border-radius:5px}
.reusable-photos__selected {position:absolute;top:7px;left:7px;border-radius:50%;background:#286c60;color:#fff;width:25px;height:25px;display:grid;place-items:center;font-weight:700;box-shadow:0 1px 5px #79958f}
.reusable-photos__preview:focus-visible,.reusable-photos__check:focus-within {outline:2px solid #2e6eaa;outline-offset:2px}
.reusable-photos__video {display:flex;gap:7px;align-items:center;justify-content:center;height:100%;color:#38556b}
.reusable-photos__body {padding:10px}
.reusable-photos__sources {font-size:12px;color:#667782}
.reusable-photos__source {display:flex;gap:6px;flex-wrap:wrap;align-items:baseline}
.reusable-photos__active,.reusable-photos__annulled {font-size:10px;line-height:1.5;border-radius:4px;padding:0 4px}
.reusable-photos__active {background:#edf6f2;color:#2d6556}
.reusable-photos__annulled {background:#f1f2f4;color:#68727c}
.reusable-photos__check {display:flex;gap:8px;align-items:center;margin:10px 0 0;font-size:13px;cursor:pointer;min-height:28px}
.reusable-photos__check input {flex-shrink:0;width:17px;height:17px}
.reusable-photos__note {margin-top:12px}
.reusable-photos__error {display:flex;align-items:center;justify-content:space-between;gap:10px;background:#fff3e0;border:1px solid #edcf9c;border-radius:8px;padding:10px;font-size:13px;margin-bottom:12px}
@media(max-width:480px) {.reusable-photos {padding:12px}.reusable-photos__head {flex-wrap:wrap}.reusable-photos__actions {justify-content:flex-start}.reusable-photos__grid {grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.reusable-photos__preview {height:110px}.reusable-photos__body {padding:8px}.reusable-photos__check {font-size:12px;min-height:36px}}
</style>
